import React, { useState } from 'react';
import { ShieldCheck, RefreshCw, Check, X } from 'lucide-react';

const BASE = process.env.REACT_APP_API_URL || '';
const TOKEN_KEY = 'sentinel.adminToken';

interface ReviewItem {
  id: number;
  artifact_type: string;
  candidate: any;
  status: string;
  created_at: string;
}
interface ModelRow {
  id: number;
  name: string;
  version: string;
  is_active: boolean;
  n_train: number | null;
  metrics: any;
  trained_at: string | null;
}

/**
 * Admin-only diagnosis & training console (Phase 6/7): review the queue of candidate
 * analyses/predictions, score + approve/reject them (which become training data), and
 * inspect pipeline health + the model registry. Auth via the shared ADMIN_TOKEN.
 */
export function Diagnosis() {
  const [token, setToken] = useState<string>(() => localStorage.getItem(TOKEN_KEY) || '');
  const [review, setReview] = useState<ReviewItem[]>([]);
  const [models, setModels] = useState<ModelRow[]>([]);
  const [metrics, setMetrics] = useState<Record<string, any> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [scores, setScores] = useState<Record<number, string>>({});

  const headers = { 'X-Admin-Token': token, 'Content-Type': 'application/json' };

  async function load() {
    if (!token) { setError('Enter the admin token first.'); return; }
    localStorage.setItem(TOKEN_KEY, token);
    setLoading(true);
    setError(null);
    try {
      const [r, m, d] = await Promise.all([
        fetch(`${BASE}/api/review?status=pending`, { headers }),
        fetch(`${BASE}/api/models`, { headers }),
        fetch(`${BASE}/api/admin/diag`, { headers }),
      ]);
      if (r.status === 401 || m.status === 401) throw new Error('Unauthorized — check the admin token.');
      setReview((await r.json()).items || []);
      setModels((await m.json()).models || []);
      setMetrics(d.ok ? (await d.json()).metrics || null : null);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  async function act(id: number, action: 'approve' | 'reject') {
    const score = scores[id] ? parseFloat(scores[id]) : undefined;
    await fetch(`${BASE}/api/review/${id}/${action}`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ admin_score: score }),
    });
    load();
  }

  return (
    <div className="stack" style={{ gap: 'var(--space-6)' }}>
      <div>
        <span className="eyebrow">Admin</span>
        <h1 className="t-headline-md" style={{ marginTop: 'var(--space-2)', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <ShieldCheck size={20} strokeWidth={1.75} /> Diagnosis &amp; Training
        </h1>
      </div>

      <div className="row" style={{ gap: 'var(--space-2)' }}>
        <input
          className="input"
          type="password"
          placeholder="Admin token"
          value={token}
          onChange={(e) => setToken(e.target.value)}
          style={{ flex: 1, minWidth: 220 }}
        />
        <button className="btn btn-primary" onClick={load} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <RefreshCw size={14} strokeWidth={1.75} /> Load
        </button>
      </div>

      {error && <p className="t-body-sm" style={{ color: 'var(--color-danger)' }}>{error}</p>}
      {loading && <p className="t-body-sm" style={{ color: 'var(--color-text-muted)' }}>Loading…</p>}

      {metrics && (
        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
          {Object.entries(metrics).map(([k, v]) => (
            <span key={k} className="chip" data-tone="neutral">{k.replace(/_/g, ' ')}: {String(v)}</span>
          ))}
        </div>
      )}

      <div>
        <h2 className="t-title-sm" style={{ marginBottom: 'var(--space-3)' }}>Review queue ({review.length})</h2>
        {review.length === 0 && (
          <p className="t-body-sm" style={{ color: 'var(--color-text-muted)' }}>
            No pending items. (Publishing is gated only when REVIEW_GATE is enabled.)
          </p>
        )}
        <div className="stack" style={{ gap: 'var(--space-3)' }}>
          {review.map((item) => (
            <div key={item.id} className="card">
              <div className="row-between">
                <span className="chip" data-tone="info">{item.artifact_type}</span>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>#{item.id}</span>
              </div>
              <div style={{ fontWeight: 600, color: 'var(--color-text-primary)', marginTop: 'var(--space-1)' }}>
                {item.candidate?.topic}
              </div>
              <p className="t-body-sm" style={{ color: 'var(--color-text-secondary)' }}>
                {(item.candidate?.content || item.candidate?.prediction || '').slice(0, 240)}…
              </p>
              <div className="row" style={{ gap: 'var(--space-2)', marginTop: 'var(--space-2)', alignItems: 'center' }}>
                <input
                  className="input"
                  type="number"
                  min={0}
                  max={1}
                  step={0.05}
                  placeholder="score 0–1"
                  value={scores[item.id] || ''}
                  onChange={(e) => setScores((s) => ({ ...s, [item.id]: e.target.value }))}
                  style={{ width: 120 }}
                />
                <button className="btn btn-primary btn-sm" onClick={() => act(item.id, 'approve')} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <Check size={13} strokeWidth={1.75} /> Approve
                </button>
                <button className="btn btn-secondary btn-sm" onClick={() => act(item.id, 'reject')} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <X size={13} strokeWidth={1.75} /> Reject
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h2 className="t-title-sm" style={{ marginBottom: 'var(--space-3)' }}>Model registry ({models.length})</h2>
        {models.length === 0 && (
          <p className="t-body-sm" style={{ color: 'var(--color-text-muted)' }}>No models trained yet.</p>
        )}
        <div className="stack" style={{ gap: 'var(--space-2)' }}>
          {models.map((m) => (
            <div key={m.id} className="card">
              <div className="row-between">
                <div style={{ fontWeight: 600 }}>
                  {m.name} <span style={{ color: 'var(--color-text-muted)', fontFamily: 'var(--font-mono)' }}>v{m.version}</span>
                </div>
                {m.is_active && <span className="chip" data-tone="success">active</span>}
              </div>
              <div className="t-body-sm" style={{ color: 'var(--color-text-muted)' }}>
                n={m.n_train ?? '—'}{m.metrics ? ` · Brier ${m.metrics.brier_before?.toFixed?.(3)} → ${m.metrics.brier_after?.toFixed?.(3)}` : ''}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
